import { useState, useEffect, useRef, useCallback } from 'react'
import {
  MessageCircle, Send, ChevronLeft, Paperclip,
  Loader2, Store, User, Search, X, Archive, Trash2, RefreshCw
} from 'lucide-react'
import { chatService, ChatConversation, ChatMessage } from '../services/chatService'
import { authService } from '../services/authService'

const MessageriePage = () => {
  const [conversations, setConversations] = useState<ChatConversation[]>([])
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [activeConversation, setActiveConversation] = useState<ChatConversation | null>(null)
  const [loading, setLoading] = useState(false)
  const [sending, setSending] = useState(false)
  const [messageInput, setMessageInput] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('active')
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [hasMoreMessages, setHasMoreMessages] = useState(false)

  const getMerchantId = useCallback((): string | undefined => {
    const user = authService.getCurrentUser()
    return user?.merchantContext?.merchantId
  }, [])

  // Load conversations
  const loadConversations = useCallback(async () => {
    setLoading(true)
    try {
      const merchantId = getMerchantId()
      const params: any = { status: statusFilter }
      if (merchantId) {
        params.merchant_id = merchantId
      }
      if (searchQuery) {
        params.search = searchQuery
      }
      const data = await chatService.getConversations(params)
      setConversations(data)
    } catch (error) {
      console.error('Failed to load conversations:', error)
    } finally {
      setLoading(false)
    }
  }, [getMerchantId, statusFilter, searchQuery])

  // Load messages for a conversation
  const loadMessages = useCallback(async (conversationId: string, page: number = 1) => {
    try {
      const result = await chatService.getMessages(conversationId, page)
      if (result.success) {
        if (page === 1) {
          setMessages(result.data)
        } else {
          setMessages(prev => [...result.data, ...prev])
        }
        setCurrentPage(page)
        setHasMoreMessages(page < result.total_pages)
      }
    } catch (error) {
      console.error('Failed to load messages:', error)
    }
  }, [])

  // Load more messages (pagination)
  const loadMoreMessages = useCallback(async () => {
    if (!activeConversation || !hasMoreMessages) return
    await loadMessages(activeConversation.conversation_id, currentPage + 1)
  }, [activeConversation, hasMoreMessages, currentPage, loadMessages])

  // Initial load
  useEffect(() => {
    loadConversations()
  }, [loadConversations])

  // Load messages when active conversation changes
  useEffect(() => {
    if (activeConversation) {
      loadMessages(activeConversation.conversation_id)
      chatService.markAsRead(activeConversation.conversation_id)
    } else {
      setMessages([])
    }
  }, [activeConversation, loadMessages])

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const handleSendMessage = async () => {
    if (!messageInput.trim() || !activeConversation) return
    setSending(true)
    try {
      const message = await chatService.sendMessage(activeConversation.conversation_id, {
        content: messageInput.trim()
      })
      if (message) {
        setMessages(prev => [...prev, message])
        setConversations(prev =>
          prev.map(conv =>
            conv.conversation_id === activeConversation.conversation_id
              ? { ...conv, last_message: message, updated_at: new Date().toISOString() }
              : conv
          )
        )
      }
      setMessageInput('')
    } catch (error) {
      console.error('Failed to send message:', error)
    } finally {
      setSending(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  const handleArchive = async () => {
    if (!activeConversation) return
    const success = await chatService.archiveConversation(activeConversation.conversation_id)
    if (success) {
      setConversations(prev =>
        prev.map(conv =>
          conv.conversation_id === activeConversation.conversation_id
            ? { ...conv, status: 'archived' }
            : conv
        )
      )
      setActiveConversation(null)
    }
  }

  const handleClose = async () => {
    if (!activeConversation) return
    const success = await chatService.closeConversation(activeConversation.conversation_id)
    if (success) {
      setConversations(prev =>
        prev.map(conv =>
          conv.conversation_id === activeConversation.conversation_id
            ? { ...conv, status: 'closed' }
            : conv
        )
      )
      setActiveConversation(null)
    }
  }

  const formatTime = (dateString: string) => {
    try {
      const now = new Date()
      const date = new Date(dateString)
      const diffMs = now.getTime() - date.getTime()
      const diffMins = Math.floor(diffMs / 60000)
      const diffHours = Math.floor(diffMs / 3600000)
      const diffDays = Math.floor(diffMs / 86400000)

      if (diffMins < 1) return "À l'instant"
      if (diffMins < 60) return `Il y a ${diffMins} min`
      if (diffHours < 24) return `Il y a ${diffHours}h`
      if (diffDays < 7) return `Il y a ${diffDays}j`
      return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })
    } catch {
      return dateString
    }
  }

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch {
      return dateString
    }
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">Active</span>
      case 'archived':
        return <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded-full">Archivée</span>
      case 'closed':
        return <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">Fermée</span>
      default:
        return null
    }
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Messagerie</h1>
          <p className="text-gray-600 mt-1">Gérez les conversations avec vos clients</p>
        </div>
        <button
          onClick={loadConversations}
          className="flex items-center gap-2 px-4 py-2 text-sm text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <RefreshCw className="h-4 w-4" />
          Actualiser
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-3 min-h-[600px]">
          {/* Left panel - Conversation list */}
          <div className="lg:col-span-1 border-r border-gray-200">
            {/* Search and filters */}
            <div className="p-4 border-b border-gray-200 space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Rechercher..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              <div className="flex gap-2">
                {['active', 'archived', 'closed'].map((status) => (
                  <button
                    key={status}
                    onClick={() => setStatusFilter(status)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                      statusFilter === status
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {status === 'active' ? 'Actives' : status === 'archived' ? 'Archivées' : 'Fermées'}
                  </button>
                ))}
              </div>
            </div>

            {/* Conversation list */}
            <div className="overflow-y-auto max-h-[500px] divide-y divide-gray-100">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
                </div>
              ) : conversations.length === 0 ? (
                <div className="text-center text-gray-500 py-12">
                  <MessageCircle className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-sm">Aucune conversation</p>
                  <p className="text-xs text-gray-400 mt-1">
                    {searchQuery ? 'Essayez un autre terme de recherche' : 'En attente de nouveaux messages'}
                  </p>
                </div>
              ) : (
                conversations.map((conv) => (
                  <button
                    key={conv.conversation_id}
                    onClick={() => setActiveConversation(conv)}
                    className={`w-full px-4 py-3 flex items-start gap-3 hover:bg-gray-50 transition-colors text-left ${
                      activeConversation?.conversation_id === conv.conversation_id
                        ? 'bg-primary-50 border-l-2 border-primary-600'
                        : ''
                    }`}
                  >
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <User className="h-5 w-5 text-primary-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {conv.subject || 'Conversation'}
                        </p>
                        {conv.last_message && (
                          <span className="text-xs text-gray-400 flex-shrink-0 ml-2">
                            {formatTime(conv.last_message.created_at)}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 truncate mt-0.5">
                        {conv.user_name || conv.user_email || 'Client'}
                      </p>
                      {conv.last_message && (
                        <p className="text-xs text-gray-400 truncate mt-0.5">
                          {conv.last_message.content}
                        </p>
                      )}
                      <div className="flex items-center gap-2 mt-1">
                        {getStatusBadge(conv.status)}
                      </div>
                    </div>
                    {conv.unread_count > 0 && (
                      <span className="flex-shrink-0 h-5 min-w-[20px] px-1.5 bg-primary-600 text-white text-xs font-bold rounded-full flex items-center justify-center">
                        {conv.unread_count}
                      </span>
                    )}
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Right panel - Messages */}
          <div className="lg:col-span-2 flex flex-col">
            {activeConversation ? (
              <>
                {/* Conversation header */}
                <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <User className="h-5 w-5 text-primary-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {activeConversation.subject || 'Conversation'}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      {activeConversation.user_name || activeConversation.user_email || 'Client'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {activeConversation.status === 'active' && (
                      <>
                        <button
                          onClick={handleArchive}
                          className="p-2 text-gray-400 hover:text-yellow-600 transition-colors"
                          title="Archiver"
                        >
                          <Archive className="h-4 w-4" />
                        </button>
                        <button
                          onClick={handleClose}
                          className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                          title="Fermer"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Messages area */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3 max-h-[400px]">
                  {hasMoreMessages && (
                    <button
                      onClick={loadMoreMessages}
                      className="w-full text-xs text-primary-600 hover:text-primary-700 text-center py-2"
                    >
                      Charger plus de messages
                    </button>
                  )}

                  {messages.length === 0 && !loading && (
                    <div className="text-center text-gray-500 py-12">
                      <MessageCircle className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                      <p className="text-sm">Aucun message pour le moment</p>
                      <p className="text-xs text-gray-400 mt-1">Répondez au client</p>
                    </div>
                  )}

                  {messages.map((msg) => {
                    const isMerchant = msg.sender_type === 'merchant'
                    return (
                      <div
                        key={msg.message_id}
                        className={`flex ${isMerchant ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[70%] px-4 py-2.5 rounded-lg text-sm ${
                            isMerchant
                              ? 'bg-primary-600 text-white rounded-br-none'
                              : 'bg-gray-100 text-gray-900 rounded-bl-none'
                          }`}
                        >
                          {msg.message_type === 'image' && msg.file_url ? (
                            <img
                              src={msg.file_url}
                              alt="Image partagée"
                              className="max-w-full rounded mb-1"
                            />
                          ) : msg.message_type === 'file' && msg.file_name ? (
                            <div className="flex items-center gap-1 text-xs">
                              <Paperclip className="h-3 w-3" />
                              <span>{msg.file_name}</span>
                            </div>
                          ) : (
                            <p className="whitespace-pre-wrap break-words">{msg.content}</p>
                          )}
                          <p className={`text-xs mt-1 ${isMerchant ? 'text-primary-200' : 'text-gray-400'}`}>
                            {formatTime(msg.created_at)}
                          </p>
                        </div>
                      </div>
                    )
                  })}

                  <div ref={messagesEndRef} />
                </div>

                {/* Input area */}
                <div className="border-t border-gray-200 p-4">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                      aria-label="Joindre un fichier"
                    >
                      <Paperclip className="h-5 w-5" />
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*,.pdf,.doc,.docx"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0]
                        if (file && activeConversation) {
                          await chatService.sendMessage(activeConversation.conversation_id, {
                            content: `Fichier: ${file.name}`,
                            message_type: 'file',
                            file_name: file.name
                          })
                        }
                        e.target.value = ''
                      }}
                    />
                    <input
                      type="text"
                      value={messageInput}
                      onChange={(e) => setMessageInput(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="Écrivez un message..."
                      className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      disabled={sending}
                    />
                    <button
                      onClick={handleSendMessage}
                      disabled={!messageInput.trim() || sending}
                      className="p-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      aria-label="Envoyer"
                    >
                      {sending ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : (
                        <Send className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </div>
              </>
            ) : (
              /* No conversation selected */
              <div className="flex items-center justify-center h-full py-24">
                <div className="text-center">
                  <MessageCircle className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                  <h3 className="text-lg font-medium text-gray-900 mb-1">Messagerie</h3>
                  <p className="text-sm text-gray-500">
                    Sélectionnez une conversation pour voir les messages
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default MessageriePage
